import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Divider, Flex, Group, LoadingOverlay, Modal, NumberInput, Paper, Radio, ScrollArea, SimpleGrid, Space, Text, TextInput, Title } from '@mantine/core';
import classes from './Transport.module.css';
import { IoAddOutline, IoTrashOutline } from 'react-icons/io5';
// import TransportProduct from './TransportProduct';
import { notifications } from '@mantine/notifications';
import { authContext } from '../../../../../context/AuthProvider.component';
import useFetch from '../../../../../Customhooks/useFetch';
import { baseUrl } from '../../../../../Constants/constant';
import AddJourneyComponent from './AddJourney/AddJourney.component';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';

// Type definitions
interface TransportField {
    id_comp_trans: number;
    id: number | null;
    source_location_id: string;
    source_place_id: string;
    transportType_id: string | null;
    destination_location: string;
    destination_place_id: string;
    distance: any;
}

interface FormField {
    transportData: TransportField[];
    id?: number | null;
    no_of_times?: number | null;
}

// Define the ProductForm interface that was missing
interface ProductForm {
    compute_id: number,
    is_draft: number,
    step: string,
    file_name: string | null,
    transport_components: {
        no_of_times: number | null;
        compute_journey_components: {
            id: number | null;
            journey_type: string;
            source_location_id: string;
            source_place_id: string;
            transportation_id: string | null;
            destination_location: string;
            destination_place_id: string;
        }[];
    }[];
}

interface TransportProps {
    onComplete: () => void;
    onBack: () => void;
}

const Transport = ({ onBack, onComplete }: TransportProps) => {
    // State for journey type (onward/return)
    const [opened, { open, close }] = useDisclosure(false);
    const navigate = useNavigate();
    const [journeyType, setJourneyType] = useState('Onward Journey');

    const dataProcessedRef = useRef(false);
    // Form validation state
    const [isValid, setIsValid] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showErrors, setShowErrors] = useState(false);
    const [requestData, setRequestData] = useState<ProductForm | null>(null);
    const [editId, setEditId] = useState<number>()
    // const [errors, setErrors] = useState<ErrorsType>({});
    const [fileName, setFileName] = useState<string>('');
    const [saveMode, setSaveMode] = useState<'next' | 'draft' | 'skip'>('next');
    const [isLoading, setIsLoading] = useState(false);

    // Transport fields state
    const [visibleComponents, setVisibleComponents] = useState<number[]>([0]);
    const [transportFields, setTransportFields] = useState<FormField[]>([{
        transportData: []
    }]);
    const [initialProductCharacteristics, setInitialProductCharacteristics] = useState<FormField[]>([{
        transportData: []
    }]);

    const transportFieldsRef = useRef<FormField[]>([{
        transportData: []
    }]);

    // Update ref when state changes
    useEffect(() => {
        transportFieldsRef.current = transportFields;
    }, [transportFields]);

    // Auth context
    const { computeId, draftLca, setDraftLca } = useContext<any>(authContext);
    const token = localStorage.getItem("token") || "";

    // State for existing transport data
    const [existingTransportData, setExistingTransportData] = useState<any>(null);
    const [transportLoading, setTransportLoading] = useState(false);

    // Fetch transport data function
    const fetchTransportData = async (url: string) => {
        try {
            setTransportLoading(true);
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "token": token,
                },
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            
            const result = await response.json();
            setExistingTransportData(result);
        } catch (error) {
            console.error('Error fetching transport data:', error);
            showNotification('error', 'Error loading transport data');
        } finally {
            setTransportLoading(false);
        }
    };

    const loadComponentsProgressively = useCallback((components: FormField[]) => {
        const indices = components.map((_, index) => index);
        setVisibleComponents(indices);
    }, []);


    // Fetch initial data if compute ID exists
    useEffect(() => {
        if (computeId && !dataProcessedRef.current) {
            fetchTransportData(`${baseUrl}compute/lca/${computeId}?is_draft=${draftLca}`);
            dataProcessedRef.current = true; // Prevent re-fetching
        }
    }, [computeId]);


    useEffect(() => {
        if (!existingTransportData) return;

        try {
            const computeJourneys = existingTransportData?.data?.rows?.[0]?.ComputeJourneys || [];
            if (computeJourneys.length > 0) {
                // Extract the journey ID (19 in this case)
                const journeyId = computeJourneys[0]?.id;

                setEditId(journeyId); // Set the edit ID 
                setJourneyType(computeJourneys[0]?.ComputeTransports[0]?.journey_type)

                const mappedFields = computeJourneys.map((journey: any) => ({
                    id: journey.id, // Store the journey ID here
                    no_of_times: journey.no_of_times,
                    transportData: journey.ComputeTransports.map((transport: any, index: number) => ({
                        id_comp_trans: index + 1,
                        id: transport.id || null,
                        source_location_id: transport.source_location || '',
                        source_place_id: transport.source_place_id || '',
                        transportType_id: transport.transportation_id?.toString() || null,
                        destination_location: transport.destination_location || '',
                        destination_place_id: transport.destination_place_id || ''
                    }))
                }));

                // Compare with ref to avoid cycles
                if (JSON.stringify(transportFieldsRef.current) !== JSON.stringify(mappedFields)) {
                    setTransportFields(mappedFields);
                    loadComponentsProgressively(mappedFields);
                }
            }
        } catch (err) {
            console.error("Error parsing transport data:", err);
            showNotification('error', 'Error parsing transport data');
        }
    }, [existingTransportData, loadComponentsProgressively]);

    const showNotification = (type: 'success' | 'error', message: string) => {
        notifications.show({
            title: type === 'success' ? 'Success' : 'Error',
            message,
            color: type === 'success' ? 'green' : 'red',
            classNames: classes,
        });
    };

    // In your handleSave method

    useEffect(() => {
        transportFieldsRef.current = transportFields;
    }, [transportFields]);


    useEffect(() => {
        dataProcessedRef.current = false;
    }, [computeId]);
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(e.target.value);

    };

    // Validate all transport fields
    const validateFields = useCallback(() => {
        const newErrors: Record<string, string> = {};
        let isFormValid = true;

        transportFieldsRef.current.forEach((field, index) => {
            if (!field.transportData || field.transportData.length === 0) {
                newErrors[`transportData_${index}`] = "At least one transport data is required";
                isFormValid = false;
            } else {
                field.transportData.forEach((transport, tIndex) => {
                    if (!transport.source_location_id) {
                        newErrors[`transportData_${index}_${tIndex}_source`] = "Source location is required";
                        isFormValid = false;
                    }

                    if (!transport.transportType_id) {
                        newErrors[`transportData_${index}_${tIndex}_type`] = "Transport type is required";
                        isFormValid = false;
                    }

                    if (!transport.destination_location) {
                        newErrors[`transportData_${index}_${tIndex}_destination`] = "Destination location is required";
                        isFormValid = false;
                    }
                });
            }
        });
        if (opened && (!fileName || fileName.trim() === '')) {
            newErrors['file_name'] = "File name is required";
            isFormValid = false;
        } else if (fileName && (fileName.length < 3 || fileName.length > 50)) {
            newErrors['file_name'] = "File name must be between 3 and 50 characters";
            isFormValid = false;
        }

        setErrors(newErrors);
        setIsValid(isFormValid);
        return isFormValid;
    }, [fileName, opened]);
    const handleTransportDataChange = useCallback((transportData: TransportField[], parentIndex: number) => {
        // Use the ref for comparison to avoid stale closures
        const currentFields = transportFieldsRef.current;
        const currentTransportData = currentFields[parentIndex]?.transportData || [];

        // Skip update if the data is the same
        if (JSON.stringify(currentTransportData) === JSON.stringify(transportData)) {
            return;
        }

        setTransportFields(prev => {
            const newFields = [...prev];

            const validatedTransportData = transportData.map((field, index) => ({
                ...field,
                id_comp_trans: field.id_comp_trans || index + 1
            }));

            newFields[parentIndex] = {
                ...newFields[parentIndex],
                transportData: validatedTransportData
            };

            return newFields;
        });

        // Validate if errors are being shown
        if (showErrors) {
            validateFields();
        }
    }, [showErrors, validateFields]);
    const handleChangeid = (index: number, field: keyof FormField, value: string) => {
        setTransportFields(prev => {
            const newFields = [...prev];
            if (field === 'id') {
                // Convert to number, handle empty string
                newFields[index].id = value ? parseInt(value, 10) : null;
            }
            if (field === 'no_of_times') {
                // Convert to number, handle empty string
                newFields[index].no_of_times = value ? parseInt(value, 10) : null;
            }
            return newFields;
        });
    };

    // Handle form submission
    const handleSave = async (e: any, mode: 'next' | 'draft' | 'skip') => {
        e.preventDefault();
        if (mode === 'skip') {
            onComplete();
            return;
        }
        setShowErrors(true);
        mode === 'next' ? setDraftLca(0) : setDraftLca(1);
        const isDraftValid = validateFields();

        // Set isDraft based on the mode
        setSaveMode(mode);

        if (!isDraftValid) return;

        // Prepare transport data
        const data: ProductForm = {
            compute_id: computeId,
            is_draft: mode === 'draft' ? 1 : 0,
            step: "Transport",
            file_name: mode === 'draft' ? fileName : null,
            transport_components: transportFields.map(field => {
                const componentJourney: any = {
                    // Include the journey ID if available
                    ...(field.id && { id: field.id }),
                    no_of_times: field.no_of_times || 1,
                    compute_journey_components: field.transportData.map(transport => {
                        const journeyData: any = {
                            journey_type: journeyType,
                            source_location: transport.source_location_id,
                            source_place_id: transport.source_place_id,
                            transportation_id: transport.transportType_id,
                            destination_location: transport.destination_location,
                            destination_place_id: transport.destination_place_id,
                        };

                        // Add transport ID if present
                        if (transport.id) {
                            journeyData.id = transport.id;
                        }

                        return journeyData;
                    })
                };

                return componentJourney;
            })
        };
        setRequestData(data);
    };

    // Handle successful API response
    const handleSuccessResponse = (result: any) => {
        if (saveMode === 'draft') {
            // Close modal first
            close();
            
            // Show success notification
            showNotification('success', 'File Saved in Draft Successfully');
            
            // Navigate to draft page
            navigate('/draft');
        } else {
            onComplete();
            showNotification('success', result?.message || 'Transport Data Saved Successfully');
        }
        
        // Reset requestData to prevent duplicate processing
        setRequestData(null);
    };

    useEffect(() => {
        const makeApiCall = async () => {
            if (requestData) {
                const url = editId ? `${baseUrl}compute/lca/updateTransport` : `${baseUrl}compute/lca/addTransport`;
                
                try {
                    setIsLoading(true);
                    
                    const response = await fetch(url, {
                        method: editId ? 'PUT' : 'POST',
                        headers: {
                            "Content-Type": "application/json",
                            "Access-Control-Allow-Origin": "*",
                            token: token,
                        },
                        body: JSON.stringify(requestData)
                    });
                    
                    if (!response.ok) {
                        throw new Error(`HTTP error! Status: ${response.status}`);
                    }
                    
                    const result = await response.json();
                    
                    // Directly handle the success response
                    handleSuccessResponse(result);
                    
                } catch (error) {
                    console.error('API Error:', error);
                    showNotification('error', 'An error occurred while saving');
                } finally {
                    setIsLoading(false);
                }
            }
        };

        makeApiCall();
    }, [requestData]);

    // Add a new journey
    const addJourney = () => {
        setTransportFields(prev => {
            const newFields = [...prev, { transportData: [] }];
            setVisibleComponents(prev => [...prev, newFields.length - 1]);
            return newFields;
        });
    };



    const handleDeleteComponent = (index: number) => {
        if (transportFields.length <= 1) {
            // Prevent deleting the last component
            return;
        }

        setTransportFields(prev => {
            const newFields = prev.filter((_, i) => i !== index);
            return newFields;
        });

        // Update visible components
        setVisibleComponents(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));

        // Re-validate remaining fields
        setTimeout(() => validateFields(), 0);
    };

    return (
        <>
            <LoadingOverlay
                visible={transportLoading}
                zIndex={1000}
                overlayProps={{
                    radius: 'sm',
                    blur: 2,
                    style: {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%'
                    }
                }}
                loaderProps={{ color: '#404040', type: 'bars' }}
            />
            <Title order={3}>Transport</Title>
            <Space h={'md'} />
            <Box className={classes.transportContainer}>
                <Radio.Group
                    className={classes.transportradio}
                    value={journeyType}
                    onChange={setJourneyType}
                >
                    <Group mt="sm" className={classes.radioGroup}>
                        <Radio
                            value="Onward Journey"
                            label="Onward Journey"
                            color="#404040"
                            classNames={{ label: classes.radioLabel }}
                        />
                        <Radio
                            value="Return Journey"
                            label="Return Journey"
                            color="#404040"
                            classNames={{ label: classes.radioLabel }}
                        />
                    </Group>
                </Radio.Group>

                <Button
                    variant="default"
                    radius={6}
                    size="md"
                    gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                    leftSection={<IoAddOutline size={20} />}
                    onClick={addJourney}
                >
                    Add Journey
                </Button>
            </Box>
            <Space h={'xl'} />
            <ScrollArea h={350} scrollbarSize={6}>
                {transportFields.map((field, index) => (
                    // Only render if this component index is in visibleComponents array
                    visibleComponents.includes(index) && (
                        <React.Fragment key={index}>
                            <Paper shadow="sm" withBorder p="md">
                                <Box className={classes.journeyName}>
                                    <Text size="xl" pb={5} fw={500}>Journey {index + 1}</Text>
                                    <>
                                        {transportFields.length > 1 && (
                                            <Box className={classes.delete_box}>
                                                <IoTrashOutline fill='red' size={25} onClick={() => handleDeleteComponent(index)} />
                                            </Box>
                                        )}
                                    </>
                                </Box>
                                <AddJourneyComponent
                                    key={`journey-${index}`}
                                    onTransportFieldsChange={(transportData: any) => handleTransportDataChange(transportData, index)}
                                    parentIndex={index}
                                    initialFields={field.transportData}
                                    showErrors={showErrors}
                                    computeId={computeId}
                                />
                                <TextInput
                                    value={field.id || ''}
                                    onChange={(e) => handleChangeid(index, 'id', e.target.value)}
                                    style={{ display: 'none' }}
                                />
                                <SimpleGrid cols={4}>
                                    <NumberInput
                                        // variant= "unstyled"
                                        label="Want to repeat this journey ×"
                                        value={field.no_of_times || '1'}
                                        onChange={(value: any) => handleChangeid(index, 'no_of_times', value)}
                                        min={1}
                                        max={20}
                                    // placeholder="Want to repeat this journey ×"
                                    />
                                </SimpleGrid>

                            </Paper>
                            <Space h={'lg'} />
                        </React.Fragment>
                    )
                ))}
            </ScrollArea>
            <Modal centered opened={opened} onClose={close} title="Save in Draft">
                <TextInput
                    autoFocus
                    label="File Name"
                    value={fileName}
                    onChange={handleChange}
                    variant="filled"
                    withAsterisk
                    mb="lg"
                    placeholder="Please enter file name"
                    radius={6}
                    error={errors?.file_name}
                />
                <Divider h={'md'} />
                <Flex justify="flex-end">
                    <Button
                        variant="gradient"
                        gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                        radius={6}
                        onClick={(e) => handleSave(e, 'draft')}
                        size="md"
                    >
                        Save
                    </Button>
                </Flex>

            </Modal>
            <Flex
                justify="flex-end"
            >
                <Group >
                    <Button
                        variant="default"
                        radius={6}
                        size="md"
                        onClick={open}
                    >
                        Save to Draft
                    </Button>
                    <Button
                        variant="default"
                        radius={6}
                        size="md"
                        onClick={onBack}
                    >
                        Back
                    </Button>
                    <Button
                        variant="default"
                        radius={6}
                        onClick={(e) => handleSave(e, 'skip')}
                        size="md"
                    >
                        Skip
                    </Button>
                    <Button
                        variant="gradient"
                        gradient={{ from: '#404040', to: '#404040', deg: 90 }}
                        radius={6}
                        onClick={(e) => handleSave(e, 'next')}
                        loading={isLoading}
                        disabled={isLoading}
                        size="md"
                    >
                        Next
                    </Button>
                </Group>
            </Flex>
        </>
    );
};

export default memo(Transport);

